import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizeImage from 'tiptap-extension-resize-image';
import SignatureBlock from './SignatureBlock';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Undo,
  Redo,
  IndentIncrease,
  IndentDecrease,
  Table as TableIcon,
  Rows,
  Columns,
  Trash2,
  Palette,
  Highlighter,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

// The resize-image extension registers its node under this name (NOT 'image').
// Centralised so any future rename of the underlying extension needs one edit,
// not five — this exact pitfall bit us in two consecutive iterations.
const IMAGE_NODE = 'imageResize';

const FONT_COLORS = [
  { name: 'Default', value: null },
  { name: 'Black', value: '#0f172a' },
  { name: 'Slate', value: '#475569' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Amber', value: '#d97706' },
  { name: 'Green', value: '#16a34a' },
  { name: 'Teal', value: '#0d9488' },
  { name: 'Blue', value: '#2563eb' },
  { name: 'Indigo', value: '#4f46e5' },
  { name: 'Purple', value: '#9333ea' },
  { name: 'Pink', value: '#db2777' },
];

const HIGHLIGHT_COLORS = [
  { name: 'None', value: null },
  { name: 'Yellow', value: '#fef08a' },
  { name: 'Green', value: '#bbf7d0' },
  { name: 'Blue', value: '#bfdbfe' },
  { name: 'Pink', value: '#fbcfe8' },
  { name: 'Orange', value: '#fed7aa' },
  { name: 'Gray', value: '#e5e7eb' },
];

const RichTextEditor = ({
  content,
  onChange,
  placeholder = 'Type your message...',
  onPasteImage,
  minHeight = 120,
  enableTable = false,
}) => {
  // Pending insert queue for images that paste while the async upload runs.
  // We record the caret position at paste time and honor it after the upload
  // completes — otherwise the image would land at whatever position the user
  // has scrolled/clicked to during the wait.
  const editorRef = useRef(null);

  const extensions = [
    StarterKit,
    ResizeImage,
    SignatureBlock,
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
  ];
  if (enableTable) {
    extensions.push(
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    );
  }

  const editor = useEditor({
    extensions,
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      handlePaste: (view, event) => {
        try {
          const items = Array.from(event.clipboardData?.items || []);
          const imgItem = items.find((it) => (it.type || '').startsWith('image/'));
          if (imgItem && typeof onPasteImage === 'function') {
            const file = imgItem.getAsFile();
            if (file) {
              // Capture the caret at paste time so the async upload lands the
              // image where the user pasted, not at the very end.
              const pos = view.state.selection.from;
              const insertAt = (attachment) => {
                if (!editorRef.current || !attachment?.url) return;
                editorRef.current
                  .chain()
                  .focus()
                  .insertContentAt(pos, {
                    type: IMAGE_NODE,
                    attrs: { src: attachment.url, alt: attachment.filename || 'pasted-image' },
                  })
                  .run();
              };
              onPasteImage(file, insertAt);
              return true;
            }
          }
        } catch (e) {
          /* ignore */
        }
        return false;
      },
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none p-3 border border-slate-200 rounded-md crm-rich-content',
        style: `min-height: ${minHeight}px;`,
      },
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    const next = content || '';
    if (editor.getHTML() !== next) {
      editor.commands.setContent(next, false);
    }
  }, [editor, content]);

  const [colorOpen, setColorOpen] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);

  if (!editor) return null;

  const inTable = editor.isActive('table');
  // The tiptap-extension-resize-image package registers its node as
  // The resize-image extension registers as `IMAGE_NODE` (see top of file).
  const inImage = editor.isActive(IMAGE_NODE);
  const currentColor = editor.getAttributes('textStyle').color;
  const currentHighlight = editor.getAttributes('highlight').color;

  // Preset widths for inline images. `null` clears the width so the image
  // renders at its natural size. `100%` gives a fluid, container-width fit.
  const IMAGE_PRESETS = [
    { label: 'S', title: 'Small (200px)', width: '200px' },
    { label: 'M', title: 'Medium (400px)', width: '400px' },
    { label: 'Fit', title: 'Best fit (600px)', width: '600px' },
    { label: 'Full', title: 'Full width', width: '100%' },
  ];

  const applyImageWidth = (w) => {
    if (!editor) return;
    // Explicit height='auto' keeps the aspect ratio for the browser and email
    // clients regardless of the source dimensions.
    editor.chain().focus().updateAttributes(IMAGE_NODE, { width: w, height: 'auto' }).run();
  };

  const applyColor = (val) => {
    if (val) editor.chain().focus().setColor(val).run();
    else editor.chain().focus().unsetColor().run();
    setColorOpen(false);
  };

  const applyHighlight = (val) => {
    if (val) editor.chain().focus().toggleHighlight({ color: val }).run();
    else editor.chain().focus().unsetHighlight().run();
    setHighlightOpen(false);
  };

  const menuBar = (
    <div className="flex items-center gap-1 p-2 border-b border-slate-200 bg-slate-50 rounded-t-md flex-wrap">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'bg-slate-200' : ''}
        title="Bold"
        data-testid="rte-bold"
      >
        <Bold className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'bg-slate-200' : ''}
        title="Italic"
        data-testid="rte-italic"
      >
        <Italic className="w-4 h-4" />
      </Button>

      <Popover open={colorOpen} onOpenChange={setColorOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            title="Text color"
            data-testid="rte-color"
            className="relative"
          >
            <Palette className="w-4 h-4" />
            <span
              aria-hidden
              className="absolute bottom-1 left-1/2 -translate-x-1/2 w-4 h-1 rounded-sm"
              style={{ backgroundColor: currentColor || '#0f172a' }}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="start">
          <div className="text-xs font-medium text-slate-500 mb-2 px-1">Text color</div>
          <div className="grid grid-cols-6 gap-1.5">
            {FONT_COLORS.map((c) => (
              <button
                key={c.value || 'default'}
                type="button"
                title={c.name}
                onClick={() => applyColor(c.value)}
                data-testid={`rte-color-${c.name.toLowerCase()}`}
                className={`w-7 h-7 rounded border transition-all hover:scale-110 ${
                  currentColor === c.value ? 'ring-2 ring-indigo-500 ring-offset-1' : 'border-slate-200'
                }`}
                style={{ backgroundColor: c.value || '#ffffff' }}
              >
                {!c.value && <span className="text-[10px] text-slate-400">×</span>}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <Popover open={highlightOpen} onOpenChange={setHighlightOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            title="Highlight"
            data-testid="rte-highlight"
            className={editor.isActive('highlight') ? 'bg-slate-200' : ''}
          >
            <Highlighter className="w-4 h-4" style={{ color: currentHighlight || undefined }} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="start">
          <div className="text-xs font-medium text-slate-500 mb-2 px-1">Highlight</div>
          <div className="grid grid-cols-6 gap-1.5">
            {HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c.value || 'none'}
                type="button"
                title={c.name}
                onClick={() => applyHighlight(c.value)}
                data-testid={`rte-highlight-${c.name.toLowerCase()}`}
                className={`w-7 h-7 rounded border transition-all hover:scale-110 ${
                  currentHighlight === c.value ? 'ring-2 ring-indigo-500 ring-offset-1' : 'border-slate-200'
                }`}
                style={{ backgroundColor: c.value || '#ffffff' }}
              >
                {!c.value && <span className="text-[10px] text-slate-400">×</span>}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <div className="w-px h-6 bg-slate-300 mx-1" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'bg-slate-200' : ''}
        title="Bulleted list"
      >
        <List className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'bg-slate-200' : ''}
        title="Numbered list"
      >
        <ListOrdered className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
        title="Indent"
      >
        <IndentIncrease className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().liftListItem('listItem').run()}
        title="Outdent"
      >
        <IndentDecrease className="w-4 h-4" />
      </Button>

      {enableTable && (
        <>
          <div className="w-px h-6 bg-slate-300 mx-1" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            }
            title="Insert 3×3 table"
            data-testid="rte-insert-table"
          >
            <TableIcon className="w-4 h-4" />
          </Button>
          {inTable && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => editor.chain().focus().addRowAfter().run()}
                title="Add row below"
                data-testid="rte-add-row"
              >
                <Rows className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                title="Add column right"
                data-testid="rte-add-col"
              >
                <Columns className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => editor.chain().focus().deleteTable().run()}
                title="Delete table"
                className="text-rose-600 hover:bg-rose-50"
                data-testid="rte-delete-table"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </>
      )}

      {/* Image size presets — shown only when an <img> is currently selected. */}
      {inImage && (
        <>
          <div className="w-px h-6 bg-slate-300 mx-1" />
          <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium px-1">
            Image
          </span>
          {IMAGE_PRESETS.map((p) => (
            <Button
              key={p.label}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => applyImageWidth(p.width)}
              title={p.title}
              data-testid={`rte-image-${p.label.toLowerCase()}`}
              className="px-2 min-w-[36px] text-xs font-semibold"
            >
              {p.label}
            </Button>
          ))}
        </>
      )}

      <div className="w-px h-6 bg-slate-300 mx-1" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        <Undo className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        <Redo className="w-4 h-4" />
      </Button>
    </div>
  );

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      {menuBar}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
